import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { fleet, type CandidateAsset, type CommandSelector } from "@/api/client";
import { CompanyFilter } from "@/components/CompanyFilter";
import { Field } from "@/components/Field";
import { Select, SearchInput } from "@/components/inputs";
import { FilePick } from "@/components/FilePick";
import { Icon } from "@/components/icons";
import { assetData } from "@/api/client";
import { CAP, useSession } from "@/auth/session";
import { COMMANDS, actionClass, toneVar } from "@/modules/fleet/catalogue";

/** New run — FOTA's "New rollout" box, with a command selector on top.
 *
 *  MIRRORED ON PURPOSE (Dattatray: *"Make surr we mirror fota UI as much as
 *  possible with and addtion of commadn selection"*). Same numbered fields,
 *  same "N shown · M selected" line, same tick-box table with a select-all in
 *  the header, same name field and count-labelled create button. An operator
 *  who has rolled firmware already knows this screen.
 *
 *  TWO WAYS TO NAME THE TARGETS, ONE AT A TIME. FOTA has only the filter; a
 *  command run also needs the pasted/CSV list Dattatray asked for. They are a
 *  SEGMENTED CHOICE rather than two live panels, because two populated target
 *  lists mean the button's blast radius depends on which you touched last —
 *  the one-element-two-forms bug this console has shipped before.
 *
 *  The server takes either and runs the same code afterwards, so a ticked run
 *  and a filtered run cannot diverge in what they check.
 */

type Mode = "list" | "bulk";

export function NewRun({ onCreated, focus }: {
  onCreated: (batchId: string) => void;
  /** ONE ASSET, arrived at from the map or a deep link. The run builder opens
   *  scoped to it and with it already ticked, so "Create run" targets exactly
   *  the vehicle the operator clicked.
   *
   *  Dattatray: *"the link from asset data page shall open only that asset in
   *  New Run Section"*. The alternative was a second command surface for one
   *  asset, which is how two command pickers appeared on this page once
   *  already - and two places that stop vehicles drift apart. A single asset
   *  is a run of one. */
  focus?: { assetId: string; company: string } | null;
}) {
  // THE WRITE IS GATED HERE TOO, not only on the server. `require_cap`
  // refuses the call either way, but a button that looks live and returns 403
  // teaches an operator nothing — the same nav-says-yes-server-says-no gap
  // that moved this module out of Asset Data (D-111).
  const { can } = useSession();
  const mayCommand = can(CAP.IMMOBILIZE);
  const [cmdKey, setCmdKey] = useState("immobilize");
  const spec = COMMANDS.find((c) => c.key === cmdKey) ?? COMMANDS[0];
  const [mode, setMode] = useState<Mode>("list");

  const [company, setCompany] = useState("");
  const [assetType, setAssetType] = useState("");
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 1);
  const [name, setName] = useState("");
  const [wave, setWave] = useState("");
  //: "" = let the server pick the default for this action, so the console
  //: never restates 24h/15min as its own constant and drifts from it
  const [lifeMin, setLifeMin] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");

  const companies = useQuery({
    queryKey: ["fleet", "companies"], queryFn: assetData.companies });
  // WRAPPED, not passed by reference: `assetTypes(company?)` takes an
  // argument, and react-query calls its queryFn with a CONTEXT object — which
  // would arrive as the company filter.
  const types = useQuery({
    queryKey: ["fleet", "asset-types", company],
    queryFn: () => assetData.assetTypes(company || undefined) });

  // ...unless the server already answered the question. A scoped caller sees
  // one company and CompanyFilter collapses to a label rather than a select,
  // so there would be nothing to pick: adopt it as soon as it arrives.
  useEffect(() => {
    const list = companies.data?.companies ?? [];
    if (!company && companies.data?.scoped && list.length === 1) {
      setCompany(list[0].company_id);
    }
  }, [companies.data, company]);

  // ADOPT THE FOCUSED ASSET: its company answers the Owner question, and its
  // id goes in the search, so the list resolves to exactly that one row.
  useEffect(() => {
    if (!focus) return;
    setCompany(focus.company);
    setQ(focus.assetId);
  }, [focus?.assetId, focus?.company]);

  const selector: CommandSelector = useMemo(() => ({
    ...(company ? { company } : {}),
    ...(assetType ? { asset_type: assetType } : {}),
    ...(state ? { state } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  }), [company, assetType, state, q]);

  // THE SAME FILTER the run will use. FOTA's `_candidate_conds` exists so the
  // picker, the count and the batch cannot disagree; this is the client half
  // of that — what is listed here is what the server resolves.
  // A COMPANY IS REQUIRED BEFORE ANYTHING IS LISTED. Without it the page
  // would open showing every asset Orbit can reach, pre-selected for a
  // command that stops vehicles.
  const cands = useQuery({
    queryKey: ["fleet", "candidates", selector],
    queryFn: () => fleet.candidates(selector),
    // NOT WHEN FOCUSED. The picker is not rendered at all in that mode, so
    // this would be a round trip whose result nothing displays -- and the
    // query still became ENABLED, because adopting the focused asset sets
    // `company`. Caught by a test asserting no candidate call, which is the
    // only way an invisible request shows up.
    enabled: mode === "list" && company !== "" && !focus,
  });
  const shown: CandidateAsset[] = cands.data?.assets ?? [];
  const total = cands.data?.total ?? 0;

  // ...and tick it ONCE, when the asset changes - not every time the list
  // does.
  //
  // THIS WAS A REAL BUG AND IT IS WORTH NAMING. The dependency used to include
  // `shown`, and the body REPLACED the whole selection
  // (`setPicked([focus.assetId])`). So every refresh of the candidate list
  // threw away whatever the operator had ticked: add two assets, type one
  // character in the search, and the selection collapsed back to one. Unticking
  // the focused asset was impossible too - the next list refresh put it back.
  // `focus` never clears, so it lasted the whole visit.
  //
  // Keyed on the ASSET now, so arriving at a vehicle selects it and everything
  // after that belongs to the operator.
  useEffect(() => {
    if (!focus) return;
    setPicked([focus.assetId]);
  }, [focus?.assetId]);

  const pastedIds = text.split(/[\s,]+/).map((x) => x.trim())
    .filter((x) => x && !x.startsWith("#"));
  const targetCount = mode === "list" ? picked.length : pastedIds.length;

  const create = useMutation({
    mutationFn: () => fleet.createBatch({
      ...selector,
      ...(mode === "list" ? { asset_ids: picked } : { asset_ids: pastedIds }),
      name: name.trim() || undefined,
      lifetime_minutes: lifeMin ? Number(lifeMin) : undefined,
      action: spec.key === "mobilize" ? 1 : action,
      wave_size: wave ? Number(wave) : undefined,
    }),
    onSuccess: (r) => { setPicked([]); setText(""); onCreated(r.batch_id); },
  });

  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    if (!f) return;
    try {
      // BOM first: Excel writes one when it saves a CSV, so the header cell
      // arrives as "﻿asset_id" and would be uploaded as an asset id.
      const body = (await f.text()).replace(/^﻿/, "");
      const rows = body.split(/\r?\n/)
        .map((l) => l.split(",")[0].trim().replace(/^"(.*)"$/, "$1").trim())
        .filter((l) => l && !l.startsWith("#") && l.toLowerCase() !== "asset_id");
      if (rows.length === 0) {
        setParseError("that file has no asset rows in its first column");
        return;
      }
      setText(rows.join("\n"));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  const allShownPicked = shown.length > 0
    && shown.every((a) => picked.includes(a.asset_id));

  return (
    <div className="panel panel-pad">
      {/* 1 · THE COMMAND - the addition to FOTA's box, and it comes first
          because every field under it depends on which command this is. */}
      <Field label="1 · Command">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {COMMANDS.map((c) => (
            <button key={c.key} type="button" disabled={!c.built}
              onClick={() => { setCmdKey(c.key); setAction(c.options[0]?.action ?? 1); }}
              title={c.built ? c.hint : `${c.label} is not built yet`}
              style={{
                font: "inherit", fontSize: 12.5,
                fontWeight: c.key === cmdKey ? 700 : 500,
                padding: "7px 14px", borderRadius: 9,
                cursor: c.built ? "pointer" : "not-allowed",
                opacity: c.built ? 1 : 0.5,
                border: `1px solid ${c.key === cmdKey ? toneVar(c) : "var(--line)"}`,
                background: c.key === cmdKey
                  ? `color-mix(in srgb, ${toneVar(c)} 10%, transparent)`
                  : "transparent",
              }}>
              <Icon name={c.icon} /> {c.label}
            </button>
          ))}
        </div>
      </Field>

      {/* WHO IT APPLIES TO - fleet mode only. Arriving from the map the
          vehicle is already chosen, so an Owner / Asset type / Find / Lifecycle
          row is four controls whose only job is to choose the thing that is
          already chosen. Dattatray: *"we only show run when we nagivate from
          the asset data and make sure that that exact same asset is ready for
          a command"*. */}
      {focus ? (
        <div style={{ marginTop: 12 }}>
          <Field label="2 · Asset">
            <div className="mono" style={{ fontSize: 13, fontWeight: 700,
                  color: "var(--t-bright)", padding: "7px 0" }}>
              {focus.assetId}
              <span style={{ fontWeight: 400, fontSize: 11.5,
                             color: "var(--t-mute)", marginLeft: 8 }}>
                {focus.company}
              </span>
            </div>
          </Field>
        </div>
      ) : (
      <div style={{ display: "grid", gap: 12, marginTop: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {/* THE SHARED FILTER, not a hand-rolled select. It carries `scoped`
            straight from the endpoint that returned the list, so whether an
            "All companies" row is even offered is the SERVER's answer rather
            than a role check in the console - which is the whole reason the
            component exists. */}
        {/* NO DEFAULT COMPANY. Dattatray: *"the default selection shall not
            be All companies It should be no companies! User needs to select a
            compnay that they are allowed to select"*.
            "All companies" as the OPENING state means the widest possible
            blast radius is what you get by touching nothing - on the one page
            that stops vehicles. The operator names the company first, and the
            candidate list stays empty until they do. `allLabel` is the
            component's own hook for exactly this: "The 'no filter' row. null
            removes it - for a picker that must name one." */}
        <CompanyFilter label="2 · Owner" fill
          allLabel="Select a company"
          value={company}
          onChange={(v) => { setCompany(v); setPicked([]); }}
          companies={companies.data?.companies ?? []}
          scoped={companies.data?.scoped ?? true} />
        <Field label="3 · Asset type">
          <Select className="fill" value={assetType}
            onChange={(e) => { setAssetType(e.target.value); setPicked([]); }}>
            <option value="">Any</option>
            {/* `{asset_type, count}`, not a bare string - and showing the
                COUNT is the point: it tells you how big the filter is before
                you apply it, which is the same service the candidate list
                performs one step later. */}
            {(types.data?.asset_types ?? []).map((t) => (
              <option key={t.asset_type} value={t.asset_type}>
                {t.asset_type} ({t.count})
              </option>
            ))}
          </Select>
        </Field>
        {/* SEARCH, not just filters. The device picker had one and New run
            needed it: with thousands of assets, "the bike with this VIN" is
            the common question and no combination of company/type/state
            answers it. */}
        <Field label="4 · Find an asset">
          <SearchInput className="fill" placeholder="VIN / BIN / plate"
            value={q} onChange={(e) => { setQ(e.target.value); setPicked([]); }} />
        </Field>
        <Field label="5 · Lifecycle state">
          <Select className="fill" value={state}
            onChange={(e) => { setState(e.target.value); setPicked([]); }}>
            <option value="">Any state</option>
            <option value="deployed">deployed</option>
            <option value="inventory">inventory</option>
            <option value="repair">repair</option>
          </Select>
        </Field>
      </div>
      )}

      {/* THE LEVEL BELONGS TO BOTH MODES. Only for a command that HAS a choice
          - a release has no type - but a focused run still has to say WHICH
          immobilise, so it cannot live inside the fleet-only block. It was in
          the grid above, which is how it would have disappeared from the
          single-asset view and left the level silently defaulted. */}
      {spec.options.length > 0 && (
        <div style={{ marginTop: 12, maxWidth: 330 }}>
          <Field label={`${focus ? 3 : 6} · ${spec.label} as`}>
            <Select className="fill" value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {spec.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {/* HOW THE TARGETS ARE NAMED. Exactly one is live - and NEITHER is,
          when the target is already decided. Arriving from the map there is
          nothing to choose from and nothing to paste: a list of one with its
          only row ticked, and a CSV box beside it, are both answers to a
          question the operator already answered by clicking a vehicle. */}
      {!focus && (<>
      <div style={{ display: "flex", gap: 8, margin: "14px 0 8px", flexWrap: "wrap" }}>
        {(["list", "bulk"] as Mode[]).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            style={{
              font: "inherit", fontSize: 12.5, fontWeight: mode === m ? 700 : 500,
              padding: "6px 12px", borderRadius: 9, cursor: "pointer",
              border: `1px solid ${mode === m ? "var(--ok)" : "var(--line)"}`,
              background: mode === m
                ? "color-mix(in srgb, var(--ok) 10%, transparent)" : "transparent",
            }}>
            {m === "list" ? "Choose from list" : "Bulk: paste or CSV"}
          </button>
        ))}
      </div>

      {mode === "list" ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
              {shown.length} shown{total > shown.length ? ` of ${total}` : ""} ·{" "}
              <b style={{ color: "var(--t-bright)" }}>{picked.length} selected</b>
            </span>
            {picked.length > 0 && (
              <button className="btn ghost" onClick={() => setPicked([])}>Clear</button>
            )}
            {total > shown.length && (
              <button className="btn ghost"
                title="select every asset the filter covers, not just this page"
                onClick={async () => {
                  const all = await fleet.candidates(selector, 500);
                  setPicked(all.assets.map((a) => a.asset_id));
                }}>
                Select all {total}
              </button>
            )}
          </div>
          <div className="tblwrap" style={{ maxHeight: 240, overflowY: "auto" }}>
            <table className="tbl">
              {/* STICKY. The list scrolls inside a fixed box, and a header
                  that scrolls away leaves four unlabelled columns - which is
                  exactly when you are reading them to decide what to tick. */}
              <thead style={{ position: "sticky", top: 0, zIndex: 1,
                              background: "var(--panel, var(--bg))" }}><tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox" checked={allShownPicked}
                    title="select all shown (filtered)"
                    onChange={(e) => {
                      const ids = shown.map((a) => a.asset_id);
                      setPicked(e.target.checked
                        ? [...new Set([...picked, ...ids])]
                        : picked.filter((id) => !ids.includes(id)));
                    }} />
                </th>
                <th>Asset</th><th>Company</th><th>Type</th><th>State</th>
              </tr></thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="cap">
                    {!company
                      ? "Choose a company to list its assets."
                      : cands.isLoading
                        ? "…"
                        : "No asset matches, or none has a telematics box fitted."}
                  </td></tr>
                )}
                {shown.map((a) => (
                  <tr key={a.asset_id}>
                    <td>
                      <input type="checkbox" checked={picked.includes(a.asset_id)}
                        onChange={(e) => setPicked(e.target.checked
                          ? [...picked, a.asset_id]
                          : picked.filter((id) => id !== a.asset_id))} />
                    </td>
                    <td className="mono">{a.asset_id}</td>
                    <td>{a.tenant_id}</td>
                    <td>{a.asset_type}</td>
                    <td>{a.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center",
                        justifyContent: "space-between", flexWrap: "wrap",
                        marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--t-mute)" }}>
              One asset id per row. Paste below, or load a CSV.
            </span>
            <span style={{ display: "flex", gap: 8 }}>
              <a className="btn ghost sm" href={fleet.bulkTemplateUrl(spec.key)}>
                <Icon name="download" /> Template
              </a>
              <FilePick accept=".csv,text/csv" label="Choose .csv"
                fileName={fileName} onFile={onFile} before />
            </span>
          </div>
          <textarea className="inp" rows={5} value={text}
            placeholder={"AE-2W-TEST-VIN005\nAE-2W-TEST-VIN007"}
            onChange={(e) => setText(e.target.value)} />
          {parseError && (
            <div style={{ fontSize: 11.5, color: "var(--bad)" }}>{parseError}</div>
          )}
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
            {pastedIds.length} listed
          </div>
        </>
      )}
      </>)}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end",
                    justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
        <Field label="Run name">
          <SearchInput style={{ width: 220 }} placeholder="e.g. depot 4 arrears"
            value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {/* FOTA's canary lever, same name, same meaning: how many devices this
            run may touch per tick. Blank = the worker's global budget. */}
        {/* CONFIGURABLE LIFETIME (OFL-06). It lived on the single-asset
            panel; when that panel went, the requirement would have gone with
            it. It bounds how long Orbit keeps RETRYING delivery - not how
            long the restraint lasts, which is why it does not say "expires". */}
        <Field label="Retry delivery for">
          <Select style={{ width: 150 }} value={lifeMin}
            onChange={(e) => setLifeMin(e.target.value)}>
            <option value="">Default</option>
            <option value="5">5 minutes</option>
            <option value="60">1 hour</option>
            <option value="360">6 hours</option>
            <option value="1440">24 hours</option>
            <option value="10080">7 days</option>
          </Select>
        </Field>
        <Field label="Wave size (per tick)">
          <SearchInput style={{ width: 130 }} placeholder="all"
            value={wave} onChange={(e) => setWave(e.target.value.replace(/\D/g, ""))} />
        </Field>
        <button className={actionClass(spec, true)}
          disabled={targetCount === 0 || create.isPending || !mayCommand}
          onClick={() => create.mutate()}>
          <Icon name={spec.icon} /> Create run ({targetCount})
        </button>
      </div>
      {!mayCommand && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
          You can review a selection. Creating a run needs the immobilise
          permission.
        </div>
      )}
      {create.isError && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String((create.error as Error)?.message ?? "could not create the run")}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
        A run is created as a <b>draft</b>. No hold is placed and nothing is
        sent until you press Start.
      </div>
    </div>
  );
}
